import {
  Box,
  Circle,
  Flex,
  FormControl,
  FormLabel,
  Slider,
  SliderFilledTrack,
  SliderTrack,
  SliderThumb,
  Text,
  Link,
} from '@chakra-ui/react';
import { useMemo } from 'react';
import {
  EVENT_TICKET_FIRST_SEGMENT_STOPS,
  getAppleRadiusSliderMarks,
} from './utils/appleRadiusLimits';

export { getAppleRadiusSliderMarks } from './utils/appleRadiusLimits';

function focusMovesToAnotherControl(relatedTarget: EventTarget | null) {
  if (!relatedTarget || !(relatedTarget instanceof HTMLElement)) {
    return false;
  }
  const el = relatedTarget;
  const tag = el.tagName;
  if (['INPUT', 'TEXTAREA', 'BUTTON', 'SELECT'].includes(tag)) {
    return true;
  }
  if (el.getAttribute('role') === 'slider') {
    return true;
  }
  if (el.isContentEditable) {
    return true;
  }
  return false;
}

function activeElementIsFormTextControl(el: Element | null) {
  if (!el || !(el instanceof HTMLElement)) {
    return false;
  }
  const tag = el.tagName;
  if (['INPUT', 'TEXTAREA', 'BUTTON', 'SELECT'].includes(tag)) {
    return true;
  }
  if (el.isContentEditable) {
    return true;
  }
  return false;
}

function createRadiusSliderConverters(
  marks: readonly number[],
  templateType: string
) {
  const segmentCount = marks.length - 1;
  const firstSegLastIdx = EVENT_TICKET_FIRST_SEGMENT_STOPS.length - 1;

  const valueToPercent = (value: number) => {
    const v = Math.max(marks[0], Math.min(marks[marks.length - 1], value));
    for (let i = 0; i < marks.length - 1; i++) {
      if (v >= marks[i] && v <= marks[i + 1]) {
        if (templateType === 'eventTicket' && i === 0) {
          let nearest: number = EVENT_TICKET_FIRST_SEGMENT_STOPS[0];
          let bestD = Infinity;
          for (const s of EVENT_TICKET_FIRST_SEGMENT_STOPS) {
            const d = Math.abs(s - v);
            if (d < bestD) {
              bestD = d;
              nearest = s;
            }
          }
          const idx = (
            EVENT_TICKET_FIRST_SEGMENT_STOPS as readonly number[]
          ).indexOf(nearest);
          const segmentT = idx / firstSegLastIdx;
          return (i + segmentT) * (100 / segmentCount);
        }
        const segmentPct = (v - marks[i]) / (marks[i + 1] - marks[i]);
        return (i + segmentPct) * (100 / segmentCount);
      }
    }
    return v <= marks[0] ? 0 : 100;
  };

  const percentToValue = (percent: number) => {
    const step = 5;
    const p = Math.max(0, Math.min(100, percent));
    const scaled = (p / 100) * segmentCount;
    const i = Math.min(Math.floor(scaled), segmentCount - 1);
    const t = scaled - i;
    if (templateType === 'eventTicket' && i === 0) {
      const idx = Math.round(t * firstSegLastIdx);
      const clampedIdx = Math.max(0, Math.min(firstSegLastIdx, idx));
      return EVENT_TICKET_FIRST_SEGMENT_STOPS[clampedIdx];
    }
    const raw = marks[i] + t * (marks[i + 1] - marks[i]);
    const minV = marks[0];
    const maxV = marks[marks.length - 1];
    const snapped = Math.round(raw / step) * step;
    return Math.max(minV, Math.min(maxV, snapped));
  };

  return { valueToPercent, percentToValue };
}

export type LocationRadiusFieldProps = {
  templateType: string;
  radius: number | undefined;
  radiusError?: string;
  onRadiusChange: (meters: number) => void;
  onRadiusFocus: () => void;
  onBlur: () => void;
};

export default function LocationRadiusField({
  templateType,
  radius,
  radiusError,
  onRadiusChange,
  onRadiusFocus,
  onBlur,
}: LocationRadiusFieldProps) {
  const sliderMarks = useMemo(
    () => getAppleRadiusSliderMarks(templateType),
    [templateType]
  );

  const { valueToPercent, percentToValue } = useMemo(
    () => createRadiusSliderConverters(sliderMarks, templateType),
    [sliderMarks, templateType]
  );

  return (
    <FormControl isInvalid={!!radiusError}>
      <FormLabel variant="templateBuilderLabel" mb={3}>
        Apple radius
      </FormLabel>

      <Flex
        alignItems="center"
        gap={4}
        px={2}
        pb={3}
        pt={0}
        borderRadius="8px"
        bg="white"
        transition="background-color 0.2s ease"
      >
        <Box flex="1" position="relative">
          <Box
            position="relative"
            w="100%"
            mb={1}
            top={25}
            sx={{
              '& > *': { pointerEvents: 'none' },
            }}
          >
            {Array.from({ length: sliderMarks.length - 1 }, (_, gapIndex) => {
              const leftPct =
                ((gapIndex + 0.5) / (sliderMarks.length - 1)) * 100;
              return (
                <Box
                  key={`mid-${gapIndex}`}
                  position="absolute"
                  left={`${leftPct}%`}
                  top={0}
                  transform="translateX(-50%)"
                >
                  <Box
                    w="1px"
                    h="6px"
                    bg="#CCCCCC"
                    opacity={0.75}
                    flexShrink={0}
                  />
                </Box>
              );
            })}
            {sliderMarks.map((markVal, i) => {
              const leftPct = (i / (sliderMarks.length - 1)) * 100;
              return (
                <Box
                  key={markVal}
                  position="absolute"
                  left={`${leftPct}%`}
                  top={0}
                  transform="translateX(-50%)"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                >
                  <Box w="1px" h="6px" bg="#CCCCCC" flexShrink={0} />
                  <Text
                    mt="2px"
                    fontSize="11px"
                    color="#CCCCCC"
                    whiteSpace="nowrap"
                    lineHeight={1}
                  >
                    {markVal}m
                  </Text>
                </Box>
              );
            })}
          </Box>
          <Slider
            width="100%"
            min={0}
            max={100}
            step={5}
            focusThumbOnChange={false}
            value={valueToPercent(radius ?? 80)}
            onChangeStart={onRadiusFocus}
            onChange={(percent) => {
              onRadiusFocus();
              onRadiusChange(percentToValue(percent));
            }}
          >
            <SliderTrack bg="#CDE4FF" height="5px">
              <SliderFilledTrack bg="appleBlue" />
            </SliderTrack>
            <SliderThumb
              boxSize={3.5}
              bg="white"
              border="1.5px solid #D1D1D1"
              _hover={{ boxShadow: 'md' }}
              onFocus={onRadiusFocus}
              onBlur={(e) => {
                if (focusMovesToAnotherControl(e.relatedTarget)) {
                  return;
                }
                requestAnimationFrame(() => {
                  if (activeElementIsFormTextControl(document.activeElement)) {
                    return;
                  }
                  onBlur();
                });
              }}
            />
          </Slider>
        </Box>
        <Text fontSize="14px" fontWeight="600" color="#0062CC">
          {radius ? `${radius}m` : '80m'}
        </Text>
      </Flex>
      <Flex mt={6} ml={2} alignItems="center" gap={2}>
        <Circle
          size="17px"
          bg="#C2E5CB"
          borderWidth="2px"
          borderStyle="dashed"
          borderColor="googleGreen"
          flexShrink={0}
        />
        <Text fontSize="14px" color="#4C4C4C">
          Google controls the trigger radius (~50m-1km).{' '}
          <Link
            href="https://docs.passentry.com/documentation/passes/geofencing/guide#google-wallet-geofence-radius"
            target="_blank"
            textDecoration="underline"
            color="#4C4C4C"
            fontSize="14px"
            textUnderlineOffset="2px"
            _hover={{ color: 'primaryDark' }}
            display="inline"
          >
            <Text as="span" textStyle="bodyFamilyRegular">
              Learn more
            </Text>
          </Link>
        </Text>
      </Flex>
    </FormControl>
  );
}
